/**
 * DebugInspector — the picking overlay for a Debug Session, two modes sharing
 * one layer (activated from the DebugSessionOverlay):
 *   inspect — hover highlights the element, click captures it (selector +
 *             outerHTML + computed styles + React source ref) + an element shot.
 *   pin     — click drops a numbered pin ANYWHERE (not tied to an element), and
 *             click-drag draws a numbered REGION rectangle — both take a comment
 *             ("what's wrong here" / "a button should live here"); on Done it
 *             captures a page screenshot with the pins/regions drawn on.
 *
 * The layer is pointer-events:none and picks via window capture-phase listeners
 * + elementFromPoint. While a mode is active the app is LOCKED: pointerdown /
 * mousedown / mouseup / click / dblclick are intercepted at capture phase so
 * menus don't close, buttons don't fire, and drags don't start under the picker
 * (wheel/trackpad scrolling stays live so you can reach the right spot). Its own
 * nodes carry data-debug-inspector so they're excluded from picking and capture.
 */
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { debugService } from '../../services/debugService';
import { captureElement, cssPath, isOverlayNode, type CapturedElement } from './debugInspectUtils';

/** x/y are page coordinates. Regions carry w/h; point pins omit them.
 *  selector is supplementary metadata (the element that happened to be under
 *  the point, if any) — a pin never REQUIRES an element. */
export interface Pin { n: number; x: number; y: number; w?: number; h?: number; comment: string; selector: string }
export interface InspectorResult {
  element?: CapturedElement;
  pins?: Pin[];
  screenshot?: Blob | null;
  annotated?: Blob | null;
  error?: string;
}

interface Props {
  mode: 'inspect' | 'pin';
  onComplete: (result: InspectorResult) => void;
  onCancel: () => void;
}

const Z = 2147483000;
const DRAG_THRESHOLD = 6; // px before a press becomes a region drag

interface PendingPin {
  x: number; y: number; w?: number; h?: number; // page coords
  boxLeft: number; boxTop: number;              // client coords for the comment box
  selector: string;
}

const DebugInspector: React.FC<Props> = ({ mode, onComplete, onCancel }) => {
  const [hover, setHover] = useState<{ rect: DOMRect; label: string } | null>(null);
  const [pins, setPins] = useState<Pin[]>([]);
  const [pending, setPending] = useState<PendingPin | null>(null);
  const [drag, setDrag] = useState<{ x1: number; y1: number; x2: number; y2: number } | null>(null); // client coords
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState<string | null>(null); // "Captured ✓" confirmation at picker z
  const [, setScrollTick] = useState(0);
  const pinsRef = useRef<Pin[]>([]);
  pinsRef.current = pins;
  const pendingRef = useRef(pending);
  pendingRef.current = pending;
  const pressRef = useRef<{ cx: number; cy: number; px: number; py: number } | null>(null);
  const dragRef = useRef(drag);
  dragRef.current = drag;

  const elAt = useCallback((cx: number, cy: number): Element | null => {
    const el = document.elementFromPoint(cx, cy);
    return el && !isOverlayNode(el) ? el : null;
  }, []);

  const complete = useCallback((r: InspectorResult) => { onComplete(r); }, [onComplete]);

  const inspectClick = useCallback(async (el: Element) => {
    setBusy(true);
    try {
      let element: CapturedElement;
      try {
        element = captureElement(el);
      } catch (err: any) {
        // Never die silently on an exotic node — capture what we can.
        element = {
          tag: el.tagName?.toLowerCase() || 'unknown', selector: cssPath(el), outerHTML: '',
          rect: { x: 0, y: 0, width: 0, height: 0 }, computedStyles: {}, source_ref: null,
        };
      }
      // The ELEMENT data (selector/HTML/styles/source) is the point of inspect
      // and is already captured above — confirm it immediately so the picker
      // never feels dead while the screenshot is attempted (the prod calendar
      // session saw Inspect "do nothing" because html2canvas HUNG here and the
      // element was never staged; captureCanvasBlob is now timeout-bounded).
      setFlash(`Captured ✓ ${element.selector.split('>').pop()?.trim() || element.tag}`);
      // Best-effort screenshot: ONE time-boxed element-target attempt. No
      // viewport fallback — on a page html2canvas can't render (the calendar),
      // the full-page shot fails too, so it only doubles the wait and stacks a
      // second hung render. The element is the point; paste/True get a real
      // image on those pages.
      const screenshot = await debugService.captureCanvasBlob({ target: el as HTMLElement });
      const shotNote = screenshot ? undefined
        : 'Element captured (no screenshot — this page won’t render; paste an image or use True)';
      debugService.logSessionAction('debug_session_inspect_captured', {
        selector: element.selector, source_ref: element.source_ref, has_screenshot: !!screenshot,
      });
      complete({ element, screenshot, error: shotNote });
    } finally { setBusy(false); setFlash(null); }
  }, [complete]);

  // Crosshair while picking, restored on unmount.
  useEffect(() => {
    const prev = document.body.style.cursor;
    document.body.style.cursor = 'crosshair';
    return () => { document.body.style.cursor = prev; };
  }, []);

  const cancelWithGuard = useCallback(() => {
    if (pinsRef.current.length > 0
      && !window.confirm(`Discard ${pinsRef.current.length} placed pin${pinsRef.current.length > 1 ? 's' : ''}? Use "Done" to keep them on the finding.`)) {
      return;
    }
    onCancel();
  }, [onCancel]);

  useEffect(() => {
    const block = (e: Event) => {
      if (isOverlayNode(e.target as Element)) return false; // inspector's own UI stays live
      e.preventDefault();
      e.stopPropagation();
      return true;
    };
    // pointerdown must NOT be preventDefault'ed — canceling it suppresses the
    // compatibility mousedown/mouseup this picker runs on (the region-drag
    // gesture would die). Stopping propagation alone still hides it from the app.
    const blockPropagation = (e: Event) => {
      if (isOverlayNode(e.target as Element)) return;
      e.stopPropagation();
    };

    const onMove = (e: MouseEvent) => {
      if (pendingRef.current) return; // freeze while commenting
      // Region drag in progress?
      const press = pressRef.current;
      if (mode === 'pin' && press) {
        const dx = e.clientX - press.cx;
        const dy = e.clientY - press.cy;
        if (dragRef.current || Math.hypot(dx, dy) > DRAG_THRESHOLD) {
          setDrag({ x1: press.cx, y1: press.cy, x2: e.clientX, y2: e.clientY });
        }
        return;
      }
      if (mode !== 'inspect') return; // pin mode: no element highlight — pins are free-space
      const el = elAt(e.clientX, e.clientY);
      if (!el) { setHover(null); return; }
      const r = el.getBoundingClientRect();
      setHover({ rect: r, label: `${el.tagName.toLowerCase()} · ${Math.round(r.width)}×${Math.round(r.height)}` });
    };


    const onMouseDown = (e: MouseEvent) => {
      if (!block(e)) return;
      if (mode !== 'pin' || pendingRef.current) return;
      pressRef.current = { cx: e.clientX, cy: e.clientY, px: e.pageX, py: e.pageY };
    };

    const onMouseUp = (e: MouseEvent) => {
      if (!block(e)) return;
      const press = pressRef.current;
      pressRef.current = null;
      if (mode !== 'pin' || pendingRef.current || !press) return;
      const wasDrag = !!dragRef.current;
      setDrag(null);
      if (wasDrag) {
        // Region pin: normalize to top-left page coords + size.
        const x = Math.min(press.px, e.pageX);
        const y = Math.min(press.py, e.pageY);
        const w = Math.abs(e.pageX - press.px);
        const h = Math.abs(e.pageY - press.py);
        if (w < 4 && h < 4) return; // degenerate — treat as noise
        const under = elAt(press.cx, press.cy);
        setPending({
          x, y, w, h,
          boxLeft: Math.min(e.clientX, window.innerWidth - 260),
          boxTop: Math.min(e.clientY + 16, window.innerHeight - 110),
          selector: under ? cssPath(under) : '',
        });
        setComment('');
      }
      // Point pins are placed on click (below) so a plain press+release stays one gesture.
    };

    const onClick = (e: MouseEvent) => {
      if (!block(e)) return;
      if (busy) return;
      const el = elAt(e.clientX, e.clientY);
      if (mode === 'inspect') {
        if (el) void inspectClick(el);
        return;
      }
      if (pendingRef.current) return; // comment box open — its buttons handle themselves
      if (dragRef.current) return;    // drag just finished; mouseup handled it
      // Point pin — anywhere, element optional.
      setPending({
        x: e.pageX, y: e.pageY,
        boxLeft: Math.min(e.clientX, window.innerWidth - 260),
        boxTop: Math.min(e.clientY + 16, window.innerHeight - 110),
        selector: el ? cssPath(el) : '',
      });
      setComment('');
    };

    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      e.stopPropagation();
      if (pendingRef.current) { setPending(null); setComment(''); } else { cancelWithGuard(); }
    };
    const onScroll = () => setScrollTick((t) => t + 1);

    const onDblClick = (e: Event) => { block(e); };
    window.addEventListener('mousemove', onMove, true);
    window.addEventListener('pointerdown', blockPropagation, true);
    window.addEventListener('pointerup', blockPropagation, true);
    window.addEventListener('mousedown', onMouseDown, true);
    window.addEventListener('mouseup', onMouseUp, true);
    window.addEventListener('click', onClick, true);
    window.addEventListener('dblclick', onDblClick, true);
    window.addEventListener('keydown', onKey, true);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      window.removeEventListener('mousemove', onMove, true);
      window.removeEventListener('pointerdown', blockPropagation, true);
      window.removeEventListener('pointerup', blockPropagation, true);
      window.removeEventListener('mousedown', onMouseDown, true);
      window.removeEventListener('mouseup', onMouseUp, true);
      window.removeEventListener('click', onClick, true);
      window.removeEventListener('dblclick', onDblClick, true);
      window.removeEventListener('keydown', onKey, true);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [mode, elAt, inspectClick, cancelWithGuard, busy]);

  const savePin = useCallback(() => {
    if (!pending) return;
    const n = pinsRef.current.length + 1;
    const pin: Pin = {
      n, x: pending.x, y: pending.y,
      ...(pending.w !== undefined ? { w: pending.w, h: pending.h } : {}),
      comment: comment.trim(), selector: pending.selector,
    };
    setPins((p) => [...p, pin]);
    debugService.logSessionAction('debug_session_pin_placed', {
      n, region: pending.w !== undefined, comment: pin.comment || null, selector: pin.selector || null,
    });
    setPending(null);
    setComment('');
  }, [pending, comment]);

  const finishPins = useCallback(async () => {
    setBusy(true);
    try {
      const list = pinsRef.current;
      const [screenshot, annotated] = await Promise.all([
        debugService.captureCanvasBlob(),
        list.length ? debugService.captureAnnotatedBlob(list) : Promise.resolve(null),
      ]);
      debugService.logSessionAction('debug_session_pins_done', { pins: list.length, has_annotated: !!annotated });
      complete({ pins: list, screenshot, annotated });
    } finally { setBusy(false); }
  }, [complete]);

  const btn: React.CSSProperties = { pointerEvents: 'auto', font: '12px system-ui', padding: '4px 10px', borderRadius: 6, border: '1px solid #333', cursor: 'pointer' };

  return createPortal(
    <div data-debug-inspector="true" style={{ position: 'fixed', inset: 0, zIndex: Z, pointerEvents: 'none' }}>
      {/* hover highlight — inspect mode only (pins are free-space, not element-locked) */}
      {mode === 'inspect' && hover && !pending && (
        <>
          <div style={{
            position: 'fixed', left: hover.rect.left, top: hover.rect.top, width: hover.rect.width, height: hover.rect.height,
            border: '2px solid #ef4444', background: 'rgba(239,68,68,0.12)', boxSizing: 'border-box', pointerEvents: 'none',
          }} />
          <div style={{
            position: 'fixed', left: hover.rect.left, top: Math.max(0, hover.rect.top - 22),
            background: '#ef4444', color: '#fff', font: '11px monospace', padding: '1px 6px', borderRadius: 3, pointerEvents: 'none', whiteSpace: 'nowrap',
          }}>{hover.label}</div>
        </>
      )}

      {/* live region marquee while dragging */}
      {drag && (
        <div style={{
          position: 'fixed',
          left: Math.min(drag.x1, drag.x2), top: Math.min(drag.y1, drag.y2),
          width: Math.abs(drag.x2 - drag.x1), height: Math.abs(drag.y2 - drag.y1),
          border: '2px dashed #ef4444', background: 'rgba(239,68,68,0.08)', boxSizing: 'border-box', pointerEvents: 'none',
        }} />
      )}

      {/* placed pins + regions (page coords → viewport via scroll offset) */}
      {pins.map((p) => p.w !== undefined ? (
        <div key={p.n} title={p.comment} style={{
          position: 'fixed', left: p.x - window.scrollX, top: p.y - window.scrollY, width: p.w, height: p.h,
          border: '2px solid rgba(239,68,68,0.92)', background: 'rgba(239,68,68,0.08)', boxSizing: 'border-box', pointerEvents: 'none',
        }}>
          <div style={{
            position: 'absolute', left: -14, top: -14, width: 28, height: 28, borderRadius: '50%',
            background: 'rgba(239,68,68,0.92)', color: '#fff', border: '2px solid #fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
            font: 'bold 13px system-ui',
          }}>{p.n}</div>
        </div>
      ) : (
        <div key={p.n} title={p.comment} style={{
          position: 'fixed', left: p.x - window.scrollX - 14, top: p.y - window.scrollY - 14, width: 28, height: 28, borderRadius: '50%',
          background: 'rgba(239,68,68,0.92)', color: '#fff', border: '2px solid #fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
          font: 'bold 13px system-ui', pointerEvents: 'none',
        }}>{p.n}</div>
      ))}

      {/* pending comment box */}
      {pending && (
        <div data-debug-inspector="true" style={{
          position: 'fixed', left: pending.boxLeft, top: pending.boxTop,
          width: 240, background: '#1a1a1a', color: '#fff', border: '1px solid #444', borderRadius: 8, padding: 8, pointerEvents: 'auto', zIndex: Z + 1,
        }}>
          <div style={{ font: '11px system-ui', opacity: 0.7, marginBottom: 4 }}>
            {pending.w !== undefined ? `Region #${pins.length + 1} (${Math.round(pending.w)}×${Math.round(pending.h || 0)})` : `Pin #${pins.length + 1}`} · comment
          </div>
          <input
            autoFocus value={comment} onChange={(e) => setComment(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); savePin(); } }}
            placeholder={pending.w !== undefined ? 'What should happen in this area?' : "What's wrong / missing here?"}
            style={{ width: '100%', boxSizing: 'border-box', font: '12px system-ui', padding: '5px 6px', borderRadius: 4, border: '1px solid #555', background: '#111', color: '#fff' }}
          />
          <div style={{ display: 'flex', gap: 6, marginTop: 6, justifyContent: 'flex-end' }}>
            <button style={{ ...btn, background: '#333', color: '#fff' }} onClick={() => { setPending(null); setComment(''); }}>Discard</button>
            <button style={{ ...btn, background: '#ef4444', color: '#fff', borderColor: '#ef4444' }} onClick={savePin}>Save pin</button>
          </div>
        </div>
      )}

      {/* busy veil — html2canvas on a big page takes a moment; say so. Once the
          capture lands, the veil flips to a green "Captured ✓" confirmation. */}
      {busy && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'auto', zIndex: Z + 2,
        }}>
          <div style={{
            background: flash ? '#14532d' : '#1a1a1a', color: '#fff', font: '13px system-ui', padding: '10px 18px', borderRadius: 8,
            border: flash ? '1px solid #22c55e' : '1px solid #444', maxWidth: 480, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {flash || 'Capturing…'}
          </div>
        </div>
      )}

      {/* toolbar */}
      <div data-debug-inspector="true" style={{
        position: 'fixed', top: 8, left: '50%', transform: 'translateX(-50%)', pointerEvents: 'auto',
        background: '#1a1a1a', color: '#fff', border: '1px solid #444', borderRadius: 999, padding: '6px 12px',
        display: 'flex', alignItems: 'center', gap: 10, font: '12px system-ui', boxShadow: '0 4px 16px rgba(0,0,0,0.4)',
      }}>
        <span style={{ opacity: 0.85 }}>
          {mode === 'inspect'
            ? '🔍 Click an element to capture it (the app won’t react while picking)'
            : `📍 Click = drop a pin anywhere · drag = mark a region · ${pins.length} placed`}
        </span>
        {mode === 'pin' && (
          <button style={{ ...btn, background: '#22c55e', color: '#062', borderColor: '#22c55e' }} disabled={busy} onClick={finishPins}>
            {busy ? '…' : `Done (${pins.length})`}
          </button>
        )}
        <button style={{ ...btn, background: '#333', color: '#fff' }} disabled={busy} onClick={cancelWithGuard}>Cancel · Esc</button>
      </div>
    </div>,
    document.body,
  );
};

export default DebugInspector;
